import { usePrevious } from '@sequencehq/utils'
import { dequal } from 'dequal'
import { useEffect } from 'react'
import { Atom, WritableAtom, useAtomValue, useSetAtom } from 'jotai'

// Keeps track of a value which we update on the client
// and occasionaly sync with the server.
//
// The actual server persistence of the data is handled by the consumer.
//
// The `clientCopyAtom` is the value we want to keep in sync with the server.
// The `clientUpdatedTimeAtom` is an atom that tracks the time of the last update.
// The `resetFromServerAtom` replaces the client copy with the server's value and
// clears `clientUpdatedTimeAtom`, in a single write.
// The `serverValue` is the latest value from the server.
// The `serverRequestTime` is the time of the last server request.
//
// If the `clientUpdatedTime` is greater than the `serverRequestTime`, the client
// has edited since we last talked to the server, so its copy is left alone.
//
// Otherwise, when the `serverValue` changes, the client copy is reset to it.
// The reset clears the updated time in the same write, so a value atom that
// stamps that time cannot leave the fresh copy looking edited.
//
// Consumers need to update the `clientUpdatedTimeAtom` manually when
// making client side updates.
//
export function useClientCopyJotai<T>(
  clientCopyAtom: Atom<T>,
  clientUpdatedTimeAtom: Atom<number | null>,
  resetFromServerAtom: WritableAtom<null, [NonNullable<T>], void>,
  serverValue: T,
  serverRequestTime: number = 0
): [T, number | null] {
  const clientUpdatedTime = useAtomValue(clientUpdatedTimeAtom)

  const clientCopy = useAtomValue(clientCopyAtom)

  const resetFromServer = useSetAtom(resetFromServerAtom)

  const previousValue = usePrevious(serverValue)

  useEffect(() => {
    if (
      serverValue &&
      !dequal(serverValue, previousValue) &&
      (clientUpdatedTime ?? 0) <= serverRequestTime
    ) {
      resetFromServer(serverValue)
    }
  }, [
    serverValue,
    previousValue,
    clientUpdatedTime,
    serverRequestTime,
    resetFromServer
  ])

  return [clientCopy, clientUpdatedTime]
}
